import { useNavigate } from 'react-router';

import { useError } from '@client/hooks/useError';
import { useTenantQueries } from '@client/queries/useTenantQueries';
import { useLoading } from '@client/store/loading.store';
import { EErrorType, type ITenant, type TErrorData } from '@common/types';

export const useTenants = () => {
  const { useUserTenants, useDeactivateTenant } = useTenantQueries();
  const { setError } = useError();
  const navigate = useNavigate();

  // Query for user tenants
  const { data, refetch, isLoading, isFetching, isError, error } =
    useUserTenants();

  const { setLoading } = useLoading(false);

  // Mutation for deactivating a tenant
  const deactivateMutation = useDeactivateTenant();

  const onDeactivate = async (entity: ITenant): Promise<void> => {
    try {
      setLoading(true);
      await deactivateMutation.mutateAsync(entity.id as string);
    } catch (error) {
      setError({
        data: error as TErrorData,
        description: EErrorType.FAILED_DEACTIVATE,
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const onEdit = (id: string): void => {
    navigate(`/tenants/${id}`);
  };

  return {
    loading: isLoading || isFetching,
    isError,
    error,
    entities: data?.tenants || [],
    total: data?.total || 0,
    onDeactivate,
    onEdit,
    refetch,
  };
};
